import { styled } from '@mui/material/styles';
import ToggleButton, { ToggleButtonProps } from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useSearchParams } from 'react-router-dom';

import DispatchSearchParam from '~components/Order/Selectors/DispatchSearchParam';
import { Nullable } from '~types/Nullable';

interface StatusTabFiltersProps<T> {
  value: T;
  onChange: (value: T) => void;
  filters: { value: T; label: string }[];
  persistFilterValue?: boolean;
}

// Exported so a sibling control (e.g. a "More" menu trigger) can sit in the tab row and match
export const StyledTabFilter = styled(ToggleButton)<ToggleButtonProps>(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.secondary,
  fontSize: '14px',
  minWidth: '85px',
  whiteSpace: 'nowrap',
  fontWeight: 500,
  transition: 'all .3s ease',
  padding: '6px 8px',
  borderRadius: '8px',
  '&.Mui-selected': {
    background: theme.palette.primary.main,
    // textOnBrand stays black in both modes so the label reads on the
    // mode-fixed brand-yellow selected background (textStrong flips to white
    // in dark, which would be near-invisible on yellow).
    color: theme.semantic.textOnBrand,
    '&:hover': { background: theme.palette.primary.main },
  },
  '&:hover': { background: theme.palette.action.hover },
}));

export const StatusTabFilters = <T extends string>({
  value,
  onChange,
  filters,
  persistFilterValue = false,
}: StatusTabFiltersProps<T>) => {
  const [currentSearchParams, setSearchParams] = useSearchParams();

  const handleOnChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: Nullable<string>,
  ) => {
    if (value) {
      onChange(value as T);
    }

    if (persistFilterValue && value) {
      currentSearchParams.set(DispatchSearchParam.STATUS, value);
      setSearchParams(currentSearchParams);
    }
  };

  return (
    <div>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleOnChange}
        aria-label="Status filters"
      >
        {filters.map(({ value, label }) => (
          <StyledTabFilter key={value} value={value}>
            {label}
          </StyledTabFilter>
        ))}
      </ToggleButtonGroup>
    </div>
  );
};
